package com.vamvisions.main.portfolio.presentation.document

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.vamvisions.main.core.domain.DataError
import com.vamvisions.main.core.domain.Result
import com.vamvisions.main.core.domain.preferences.Language
import com.vamvisions.main.core.presentation.i18n.toUiText
import com.vamvisions.main.core.presentation.markdown.MarkdownParser
import com.vamvisions.main.portfolio.domain.repository.DocumentRepository
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch

/**
 * Loads the document [slug] in [language], the language from the URL rather than the interface one.
 * Each route entry gets its own instance, so the slug and language never change.
 */
class DocumentViewModel(
    private val slug: String,
    private val language: Language,
    private val documentRepository: DocumentRepository,
) : ViewModel() {

    private val _state = MutableStateFlow(DocumentState())
    val state = _state.asStateFlow()

    private var job: Job? = null

    init {
        load()
    }

    fun onAction(action: DocumentAction) {
        when (action) {
            DocumentAction.Retry -> load()
        }
    }

    private fun load() {
        job?.cancel()
        job = viewModelScope.launch {
            _state.value = DocumentState(isLoading = true)
            _state.value = when (val result = documentRepository.get(slug, language)) {
                is Result.Success -> DocumentState(
                    isLoading = false,
                    title = result.data.title,
                    content = MarkdownParser.parse(result.data.markdown),
                )
                is Result.Error ->
                    if (result.error.type == DataError.Network.Type.NOT_FOUND) {
                        DocumentState(isLoading = false, notFound = true)
                    } else {
                        DocumentState(isLoading = false, error = result.error.toUiText())
                    }
            }
        }
    }
}
